<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const uiFlags = useMapGetter('messageTemplates/getUIFlags');
const analytics = useMapGetter('messageTemplates/getTemplateAnalytics');

const isLoading = computed(() => uiFlags.value.isFetchingAnalytics);

const selectedDays = ref(30);
const dayOptions = [
  { value: 7, label: t('MESSAGE_TEMPLATES.ANALYTICS.LAST_7_DAYS') },
  { value: 30, label: t('MESSAGE_TEMPLATES.ANALYTICS.LAST_30_DAYS') },
  { value: 90, label: t('MESSAGE_TEMPLATES.ANALYTICS.LAST_90_DAYS') },
];

const fetchData = () => {
  store.dispatch('messageTemplates/fetchAnalytics', {
    templateId: props.template.id,
    days: selectedDays.value,
  });
};

onMounted(fetchData);
watch(selectedDays, fetchData);

const totals = computed(() => analytics.value?.totals || {});
const sent = computed(() => totals.value.sent || 0);
const delivered = computed(() => totals.value.delivered || 0);
const read = computed(() => totals.value.read || 0);
const clicked = computed(() => totals.value.clicked || 0);

const hasData = computed(
  () => Object.keys(analytics.value || {}).length > 0 && sent.value > 0
);

const deliveryRate = computed(() => {
  if (sent.value === 0) return 0;
  return Math.round((delivered.value / sent.value) * 100);
});

const readRate = computed(() => {
  if (delivered.value === 0) return 0;
  return Math.round((read.value / delivered.value) * 100);
});

const clickRate = computed(() => {
  if (delivered.value === 0) return 0;
  return Math.round((clicked.value / delivered.value) * 100);
});

const metricCards = computed(() => [
  {
    label: t('MESSAGE_TEMPLATES.ANALYTICS.METRICS.SENT'),
    value: sent.value,
    rate: null,
    icon: 'i-lucide-send',
    color: 'text-n-blue-11',
    bgColor: 'bg-n-blue-3',
  },
  {
    label: t('MESSAGE_TEMPLATES.ANALYTICS.METRICS.DELIVERED'),
    value: delivered.value,
    rate: `${deliveryRate.value}%`,
    icon: 'i-lucide-check-check',
    color: 'text-n-teal-11',
    bgColor: 'bg-n-teal-3',
  },
  {
    label: t('MESSAGE_TEMPLATES.ANALYTICS.METRICS.READ'),
    value: read.value,
    rate: `${readRate.value}%`,
    icon: 'i-lucide-eye',
    color: 'text-n-violet-11',
    bgColor: 'bg-n-violet-3',
  },
  {
    label: t('MESSAGE_TEMPLATES.ANALYTICS.METRICS.CLICKED'),
    value: clicked.value,
    rate: `${clickRate.value}%`,
    icon: 'i-lucide-mouse-pointer-click',
    color: 'text-n-amber-11',
    bgColor: 'bg-n-amber-3',
  },
]);
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-2">
    <div
      class="relative flex flex-col w-full max-w-3xl max-h-[85vh] bg-n-background rounded-xl border border-n-weak shadow-lg"
    >
      <header
        class="flex items-center justify-between flex-shrink-0 px-6 py-4 border-b border-n-weak"
      >
        <div class="flex flex-col gap-1">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('MESSAGE_TEMPLATES.ANALYTICS.TITLE') }}
          </h3>
          <span class="text-sm text-n-slate-11">
            {{ template.name }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <select
            v-model="selectedDays"
            class="px-3 py-1.5 text-sm border rounded-lg border-n-weak bg-n-background text-n-slate-12"
          >
            <option
              v-for="option in dayOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <Button
            variant="ghost"
            size="sm"
            color="slate"
            icon="i-lucide-x"
            @click="emit('close')"
          />
        </div>
      </header>

      <div
        v-if="isLoading"
        class="flex items-center justify-center flex-1 py-16"
      >
        <Spinner />
      </div>
      <div v-else class="flex flex-col flex-1 gap-6 p-6 overflow-y-auto">
        <div
          v-if="!hasData"
          class="flex flex-col items-center justify-center py-12 text-center"
        >
          <span class="text-3xl i-lucide-bar-chart-2 text-n-slate-8 mb-3" />
          <p class="text-sm text-n-slate-11">
            {{ t('MESSAGE_TEMPLATES.ANALYTICS.NO_DATA') }}
          </p>
        </div>
        <div v-else class="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div
            v-for="card in metricCards"
            :key="card.label"
            class="flex flex-col gap-2 p-4 rounded-xl border border-n-weak"
          >
            <div class="flex items-center gap-2">
              <div
                class="flex items-center justify-center w-8 h-8 rounded-lg"
                :class="card.bgColor"
              >
                <span class="size-4" :class="[card.icon, card.color]" />
              </div>
              <span class="text-xs font-medium text-n-slate-11">
                {{ card.label }}
              </span>
            </div>
            <div class="flex items-baseline gap-2">
              <span class="text-2xl font-semibold text-n-slate-12">
                {{ card.value.toLocaleString() }}
              </span>
              <span v-if="card.rate" class="text-xs text-n-slate-10">
                {{ card.rate }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
